/* eslint-disable max-lines */
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import {
    DollarInputFormik,
    NumberInputFormik,
    TextAreaFormik,
    TextInputFormik,
} from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import classNames from 'classnames';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { DynamicFields } from 'components/valueCase/DynamicFields';
import {
    checkProductServiceType,
    compareProp,
    FundingSourcesList,
    markdownLinksHelperText,
} from 'components/valueCase/helpers';
import { useGet } from 'services/Http';

const allMeds = {
    oid: 'all.med',
    description: 'All Meds',
    generic_product_description: 'All Meds',
    brand_product_description: 'All Meds',
};

const setProdServIncentives = async (formik, value) => {
    const prodServIncentives = getIn(formik.values, 'product_service_incentives') ?? [];
    const newProdServIncentives = prodServIncentives.map((prodServ) => ({
        ...prodServ,
        incentive_amt: value,
    }));
    await formik.setFieldValue('product_service_incentives', newProdServIncentives);
};

export const ValueCaseDetails = ({
    stratRules,
    stratRuleConfigurableParams = undefined,
    setStratRuleConfigurableParams,
    setOpenSelectExistingDialog,
}) => {
    const formik = useFormikContext();
    const segments = useGet('/reference/payer_segments');
    const productServices = useGet('/reference/productservices/summaryonly');
    const productServicesWithAllMeds = [...(productServices ?? []), allMeds];
    const diagnoses = useGet('/reference/diagnoses/');

    const segmentsList = segments
        ?.map((segment) => ({
            name: segment.segment_name,
            secondaryData: segment.segment_id,
            value: segment.segment_id,
            payerId: segment.payer_id,
        }))
        .sort(compareProp('name'));

    return (
        <>
            <div className="font-semi-bold--uppercase padding__bottom--medium">Value Case</div>
            <SuggestSingleSelectFormik
                id="segment_id"
                placeholderText="Select Payer Segment"
                label="Payer Segment"
                required
                values={segmentsList}
                onChange={async (newValue) => {
                    await formik.setFieldValue('segment_id', newValue.value);
                    await formik.setFieldValue('payer_id', newValue.payerId);
                }}
                selectedItem={
                    segmentsList?.find((type) => type.value === getIn(formik.values, 'segment_id'))
                        ?.name
                }
            />
            <TextInputFormik
                id="value_case_name"
                required
                label="Value Case Name"
                placeholderText="Enter Value Case Name"
            />
            <SuggestSingleSelectFormik
                id="funding_source"
                placeholderText="Select Funding Source"
                label="Funding Source"
                required
                values={FundingSourcesList}
                onChange={(newValue) => formik.setFieldValue('funding_source', newValue.value)}
                selectedItem={
                    FundingSourcesList.find(
                        (source) => source.value === getIn(formik.values, 'funding_source'),
                    )?.name
                }
            />
            <SuggestMultiSelectFormik
                id="product_service_incentives"
                required
                placeholderText="Search Product/Service OIDS..."
                label="Product/service oids"
                isLoading={!productServices}
                values={productServicesWithAllMeds
                    ?.filter((prodServ) => {
                        const currentProdServs = new Set(
                            (getIn(formik.values, 'product_service_incentives') ?? []).map(
                                (ps) => ps.product_service_oid,
                            ),
                        );
                        if (currentProdServs.has(allMeds.oid)) {
                            return false;
                        }
                        return !currentProdServs.has(prodServ.oid);
                    })
                    .sort(compareProp('oid'))
                    .map((prodServ) => ({
                        name: `${prodServ.oid}${
                            prodServ.description ? ` (${prodServ.description})` : ''
                        }`,
                        secondaryData: checkProductServiceType(prodServ.oid),
                        product_service_oid: prodServ.oid,
                    }))}
                onChange={async (newValue) => {
                    const hasAllMed = newValue.find(
                        (prodServ) => prodServ.product_service_oid === allMeds.oid,
                    );
                    await formik.setFieldValue(
                        'product_service_incentives',
                        hasAllMed
                            ? [
                                  {
                                      product_service_oid: allMeds.oid,
                                      incentive_amt: getIn(formik.values, 'incentive_amt') ?? 0,
                                  },
                              ]
                            : newValue.map((prodServ) => ({
                                  product_service_oid: prodServ.product_service_oid,
                                  incentive_amt: getIn(formik.values, 'incentive_amt') ?? 0,
                              })),
                    );
                }}
                selectedItems={getIn(formik.values, 'product_service_incentives')?.map(
                    (prodServ) => ({
                        name: prodServ.product_service_oid,
                        ...prodServ,
                    }),
                )}
            />
            <SuggestMultiSelectFormik
                id="diagnoses"
                label="Diagnoses"
                placeholderText="Search Diagnoses..."
                isLoading={!diagnoses}
                values={diagnoses
                    ?.filter((diag) => {
                        const currentDiags = new Set(
                            (getIn(formik.values, 'diagnoses') ?? []).map((d) => d.dx_oid),
                        );
                        return !currentDiags.has(diag.oid);
                    })
                    .sort(compareProp('oid'))
                    .map((diag) => ({
                        name: diag.oid,
                        secondaryData: diag.description,
                    }))}
                onChange={(newValue) =>
                    formik.setFieldValue(
                        'diagnoses',
                        newValue.map((diag) => ({
                            dx_oid: diag.name,
                        })),
                    )
                }
                selectedItems={getIn(formik.values, 'diagnoses')?.map((diag) => ({
                    name: diag.dx_oid,
                }))}
            />
            <span className="sds_performance__flex-row">
                <DateInputFormik
                    id="start_date"
                    label="Start Date"
                    required
                    onChange={(newValue) =>
                        formik.setFieldValue(
                            'start_date',
                            formatDate(newValue, DateFormats.HYPHEN_DATE),
                        )
                    }
                />
                <DateInputFormik
                    id="end_date"
                    label="End Date"
                    required
                    onChange={(newValue) =>
                        formik.setFieldValue(
                            'end_date',
                            formatDate(newValue, DateFormats.HYPHEN_DATE),
                        )
                    }
                />
            </span>
            <div
                className={classNames(
                    'font-semi-bold--uppercase',
                    'padding__bottom--medium',
                    'padding__top--medium',
                )}
            >
                Stratification Rules
            </div>
            <SuggestSingleSelectFormik
                id="strat_rule_id"
                disableAutoPopulate
                required
                label="Stratification Rule"
                isLoading={!!stratRules}
                values={stratRules
                    ?.map((strat) => ({
                        name: strat.strat_rule_display_name,
                        ...strat,
                    }))
                    .sort(compareProp('name'))}
                onChange={async (newValue) => {
                    await formik.setFieldValue('strat_rule_id', newValue.strat_rule_id);
                    setStratRuleConfigurableParams(newValue.configurable_params);
                }}
                selectedItem={
                    stratRules?.find(
                        (strat) => strat.strat_rule_id === getIn(formik.values, 'strat_rule_id'),
                    )?.strat_rule_display_name
                }
                placeholderText="Select Stratification Rule"
                helperText="This method will be used to determine the routing logic for this Stratification Value Case."
            />
            {stratRuleConfigurableParams && (
                <DynamicFields
                    fieldPath="strat_rule_params"
                    configurableParams={stratRuleConfigurableParams}
                />
            )}
            <span
                className={classNames(
                    'sds_performance__flex-row',
                    'sds_performance__flex-row--align-center',
                    'padding__bottom--medium',
                    'padding__top--medium',
                )}
            >
                <div
                    className={classNames(
                        'font-semi-bold--uppercase',
                        'sds_performance__flex-item--truncate',
                    )}
                >
                    Performance Requirement
                </div>
                <ActionButtonText
                    label="Use existing performance requirement"
                    onClick={() => setOpenSelectExistingDialog(true)}
                    className="sds_performance__flex-item--maintain"
                />
            </span>
            <TextInputFormik
                id="performance_requirement_name"
                required
                label="Performance Requirement Name"
                placeholderText="Enter Performance Requirement Name"
            />
            <TextAreaFormik
                id="value_case_description"
                required
                label="Performance Requirement Description"
                placeholderText="Enter Performance Requirement Description..."
                helperText={markdownLinksHelperText}
            />
            <span className="sds_performance__flex-row">
                <DollarInputFormik
                    id="incentive_amt"
                    label="Incentive Amount"
                    required
                    onChange={async (e) => {
                        formik.handleChange(e);
                        const newValue = parseFloat(e.target.value);
                        if (Number.isNaN(newValue)) {
                            await formik.setFieldValue('incentive_amt', undefined);
                            await setProdServIncentives(formik, 0);
                        } else {
                            await setProdServIncentives(formik, newValue);
                        }
                    }}
                />
                <div className="sds_performance__flex-row--break" />
            </span>
            <span className="sds_performance__flex-row">
                <NumberInputFormik
                    id="performance_days"
                    required
                    label="Performance Days"
                    placeholderText="Enter Performance Days"
                    helperText="Days performance value case is active"
                />
                <NumberInputFormik
                    id="evaluation_days"
                    required
                    label="Evaluation Days"
                    placeholderText="Enter Evaluation Days"
                    helperText="Number of days after the due date before the case is evaluated. User can interact with performance requirement during evaluation days."
                />
            </span>
            <span className="sds_performance__flex-row">
                <NumberInputFormik
                    id="min_performance_days"
                    required
                    label="Minimum Required Days"
                    helperText="Minimum number of days required for the performance requirement to be eligible for evaluation."
                />
                <NumberInputFormik
                    id="min_claim_count"
                    required
                    label="Minimum Claim Count"
                    helperText="Minimum number of claims required for the performance requirement to be eligible for evaluation."
                />
            </span>
        </>
    );
};

ValueCaseDetails.propTypes = {
    stratRules: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    stratRuleConfigurableParams: PropTypes.shape({}),
    setStratRuleConfigurableParams: PropTypes.func.isRequired,
    setOpenSelectExistingDialog: PropTypes.func.isRequired,
};
